'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQ {
    id: string;
    question: string;
    answer: string;
}

const FAQSection: React.FC = () => {
    const [isHydrated, setIsHydrated] = useState(false);
    const [openFAQ, setOpenFAQ] = useState<string | null>(null);

    useEffect(() => {
        setIsHydrated(true);
    }, []);

    const faqs: FAQ[] = [
        {
            id: 'faq-1',
            question: 'What services does SitemapGenius offer?',
            answer: 'We specialize in web development, mobile app development, brand visibility services (Google Business Profile optimization and local SEO), e-commerce solutions, UI/UX design, and complete digital transformation packages. Every service is tailored to elevate your online presence and drive measurable results.'
        },
        {
            id: 'faq-2',
            question: 'How long does a typical project take?',
            answer: 'Project timelines vary by complexity: Standard websites take 3-6 weeks, mobile applications 8-12 weeks, and brand visibility campaigns show results within 2-3 months. We provide detailed timelines and milestones during the consultation phase to set clear expectations.'
        },
        {
            id: 'faq-3',
            question: 'What is your pricing structure?',
            answer: 'Our projects start from ₹30,000 for basic websites and scale based on requirements. We follow transparent, milestone-based pricing: 30% upfront, 40% at mid-project delivery, and 30% upon final completion. Every quote is customized after understanding your specific needs and goals.'
        },
        {
            id: 'faq-4',
            question: 'Do you provide post-launch support?',
            answer: 'Yes! All projects include 3 months of complimentary support covering bug fixes, minor updates, and technical assistance. We also offer extended maintenance packages for ongoing support, performance monitoring, and feature enhancements.'
        },
        {
            id: 'faq-5',
            question: 'Can you help optimize my existing website or Google Business Profile?',
            answer: 'Absolutely! We offer comprehensive audit and optimization services for existing websites and Google Business Profiles. Our Brand Visibility Lab specializes in local SEO, GBP optimization, reputation management, and review generation strategies to boost your online visibility.'
        },
        {
            id: 'faq-6',
            question: 'How do I get started?',
            answer: "Simply fill out the consultation form above with your project details, or reach out via email (sitemapgenius@gmail.com) or WhatsApp (+91 8686284396). We'll schedule a free discovery call within 24-48 hours and provide a detailed proposal tailored to your needs and budget."
        }
    ];

    const toggleFAQ = (id: string) => {
        if (!isHydrated) return;
        setOpenFAQ(openFAQ === id ? null : id);
    };

    if (!isHydrated) {
        return (
            <section className="py-16 bg-white">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="text-center mb-12 animate-pulse">
                        <div className="h-10 bg-slate-100 rounded w-3/4 mx-auto mb-4"></div>
                        <div className="h-6 bg-slate-100 rounded w-1/2 mx-auto"></div>
                    </div>
                    <div className="space-y-4">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-16 bg-slate-100 rounded-lg"></div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="py-16 bg-white border-t border-slate-100">
            <div className="max-w-4xl mx-auto px-6">
                <div className="text-center mb-12">
                    <h2 className="text-4xl font-bold text-slate-900 mb-4">
                        Frequently Asked Questions
                    </h2>
                    <p className="text-lg text-slate-600">
                        Quick answers to common questions about our services and process
                    </p>
                </div>

                <div className="space-y-4">
                    {faqs.map((faq) => (
                        <div
                            key={faq.id}
                            className="bg-white border border-slate-200 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md"
                        >
                            <button
                                onClick={() => toggleFAQ(faq.id)}
                                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-slate-50 transition-colors duration-200"
                                aria-expanded={openFAQ === faq.id}
                            >
                                <span className="font-bold text-slate-900 pr-4 text-base">
                                    {faq.question}
                                </span>
                                <Icon
                                    name="ChevronDownIcon"
                                    size={24}
                                    className={`text-indigo-600 flex-shrink-0 transition-transform duration-300 ${openFAQ === faq.id ? 'rotate-180' : ''
                                        }`}
                                />
                            </button>
                            <AnimatePresence>
                                {openFAQ === faq.id && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="px-6 pb-5 text-slate-600 leading-relaxed">
                                            {faq.answer}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                </div>

                <div className="mt-12 text-center pb-24 lg:pb-0">
                    <p className="text-slate-600 mb-4 font-medium">
                        Still have questions? We're here to help!
                    </p>
                    <button
                        onClick={() => {
                            const contactSection = document.getElementById('contact-methods');
                            contactSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-200 font-semibold hover:shadow-lg hover:-translate-y-0.5"
                    >
                        <Icon name="ArrowUpIcon" size={20} />
                        View Contact Details
                    </button>
                </div>
            </div>
        </section>
    );
};

export default FAQSection;